import isMatch from 'lodash/isMatch'
import { useMutation } from 'react-query'
import { APIError } from 'src/utils'

import { useInvoiceNumberingSequences } from '../../../hooks/useInvoiceNumberingSequences'
import { createInvoiceNumberingSequence as createNumberingSequence } from '../../../query-api'

interface CreateNewSequenceProps {
  invoiceNumberPrefix?: string | null
  invoiceNumber?: number
}

export interface UseCreateInvoiceNumberingSequenceProps {
  onError?: (error?: APIError) => void
  onSuccess?: () => void
}

export const useCreateInvoiceNumberingSequence = (props?: UseCreateInvoiceNumberingSequenceProps) => {
  const { invoiceNumberingSequences } = useInvoiceNumberingSequences()
  const { onError, onSuccess } = props || {}

  const {
    mutate: createSequence,
    isLoading: isProcessing,
    ...rest
  } = useMutation(createNumberingSequence, { onError, onSuccess })

  const createInvoiceNumberingSequence = ({ invoiceNumberPrefix, invoiceNumber }: CreateNewSequenceProps) => {
    // Check if the invoice prefix doesn't exist yet then create a new one
    const isNewSequence = !invoiceNumberingSequences.some((sequence) => sequence.prefix === invoiceNumberPrefix)

    if (isNewSequence && invoiceNumberPrefix && invoiceNumber) {
      createSequence({ base: invoiceNumber, prefix: invoiceNumberPrefix })
      return
    }

    // The default sequence (base=1, next=1, prefix=null) can be overridden before any invoice is created.
    // BE allows a POST to /invoices/number to overwrite it when that condition is met.
    const isDefaultSequenceOverridable = invoiceNumberingSequences.some((sequence) =>
      isMatch(sequence, { base: 1, next: 1, prefix: null }),
    )
    const shouldOverrideDefaultSequence =
      isDefaultSequenceOverridable && (invoiceNumberPrefix === '' || invoiceNumberPrefix === null)

    if (shouldOverrideDefaultSequence && invoiceNumber && invoiceNumber > 1) {
      createSequence({ base: invoiceNumber, prefix: null })
    }
  }

  return { createInvoiceNumberingSequence, isProcessing, ...rest }
}
